import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Button,
    Tooltip,
    TableSortLabel,
    TextField,
    Checkbox,
    Box,
    Chip,
    InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PersonIcon from "@mui/icons-material/Person";
import { NotificationManager } from "react-notifications";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import CommonLoader from "../../common/CommonLoader";
import TemperatureScannerDialog from "./TemperatureScannerDialog";
import UserSelectorDialog from "../../common/UserSelectorDialog";
import { generateTemperatureTrackerPdf } from "./TemperatureTrackerPdf";
import {
    GetTemperatureRecordsByCriteria,
    DeleteTemperatureRecord,
} from "../../../services/TemperatureTrackerService";
import { useNavigate } from "react-router-dom";
import {
    getComparator,
    stableSort,
} from "../../../../common-utilities/sortingUtils";

const TEMPERATURE_COLUMNS = [
    "currentTemperatureTop",
    "currentTemperatureMiddle",
    "currentTemperatureBottom",
    "minTemperature",
    "maxTemperature",
];

const BOOLEAN_COLUMNS = [
    "isWithinRange",
    "samplesPresent",
    "batteryLow",
    "thermometerReset",
];


const ROW_BG = ["#f5f5f5", "#ffffff"];
const ROW_BG_HOVER = "#e8edf3";
const ROW_BG_SELECTED = "#dce9f7";


const STICKY_ACTIONS = {
    position: "sticky",
    right: 0,
    borderLeft: "1px solid rgba(224,224,224,1)",
};


const toInputDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

const defaultDateRange = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return {
        startDate: toInputDate(today),
        endDate: toInputDate(tomorrow),
    };
};

const TemperatureTrackerTable = () => {
    const columns = [
        { id: "id", label: "ID" },
        { id: "dateCollected", label: "Date Collected", nowrap: true },
        { id: "equipment", label: "Equipment" },
        { id: "initials", label: "Analyst" },
        { id: "currentTemperatureTop", label: "Current Temp (°C)" },
        { id: "minTemperature", label: "Min (°C)" },
        { id: "maxTemperature", label: "Max (°C)" },
        { id: "isWithinRange", label: "In Range?" },
        { id: "samplesPresent", label: "Samples", tooltip: "Samples Present?" },
        { id: "batteryLow", label: "Battery", tooltip: "Is Battery Low?" },
        { id: "thermometerReset", label: "Reset", tooltip: "Did You Reset The Thermometer?" },
        { id: "actions", label: "Actions" },
    ];

    const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

    const [recordsData, setRecordsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    // Newest records first, matching how the lab reviews the log.
    const [orderBy, setOrderBy] = useState("id");
    const [order, setOrder] = useState("desc");
    const [tableHeight, setTableHeight] = useState(600);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const tableContainerRef = useRef(null);

    const [searchText, setSearchText] = useState("");
    // Analysts picked in the user selector; filters on the record's Initials (operator).
    const [selectedAnalysts, setSelectedAnalysts] = useState([]);
    const [analystDialogOpen, setAnalystDialogOpen] = useState(false);
    const [startDate, setStartDate] = useState(defaultDateRange().startDate);
    const [endDate, setEndDate] = useState(defaultDateRange().endDate);

    const [searchCriteria, setSearchCriteria] = useState({
        searchText: "",
        analysts: "",
        ...defaultDateRange(),
    });

    const navigate = useNavigate();

    useEffect(() => {
        const calculateTableHeight = () => {
            if (tableContainerRef.current) {
                const containerTop = tableContainerRef.current.getBoundingClientRect().top;
                const windowHeight = window.innerHeight;
                const availableHeight = windowHeight - containerTop - 76;
                setTableHeight(Math.max(300, Math.min(availableHeight, windowHeight - 200)));
            }
        };
        calculateTableHeight();
        window.addEventListener("resize", calculateTableHeight);
        const timeoutId = setTimeout(calculateTableHeight, 100);
        return () => {
            window.removeEventListener("resize", calculateTableHeight);
            clearTimeout(timeoutId);
        };
    }, [recordsData]);

    useEffect(() => {
        fetchRecords(searchCriteria);
    }, [isDeleted]);

    const fetchRecords = async (criteria) => {
        try {
            setLoading(true);
            const response = await GetTemperatureRecordsByCriteria(
                criteria.startDate,
                criteria.endDate,
                "",
                criteria.searchText,
                criteria.analysts
            );
            setRecordsData(response.results || []);
            setSelectedIds([]);
        } catch (error) {
            console.error("Error fetching temperature records:", error);
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(
        () => stableSort(recordsData, getComparator(order, orderBy)),
        [recordsData, order, orderBy]
    );

    const pageData = useMemo(
        () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page, rowsPerPage]
    );

    const selectedRecords = useMemo(
        () => sortedData.filter((r) => selectedIds.includes(r.id)),
        [sortedData, selectedIds]
    );

    const pageSelectedCount = pageData.filter((r) => selectedIds.includes(r.id)).length;
    const allPageSelected = pageData.length > 0 && pageSelectedCount === pageData.length;

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (!startDate || !endDate) {
            NotificationManager.error("Invalid Dates");
            return;
        }
        const analysts = selectedAnalysts.map((u) => u.operator.trim()).join(",");
        const updated = { searchText, analysts, startDate, endDate };
        setSearchCriteria(updated);
        setPage(0);
        fetchRecords(updated);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const handleToggleRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleTogglePage = () => {
        const pageIds = pageData.map((r) => r.id);
        setSelectedIds((prev) =>
            allPageSelected
                ? prev.filter((id) => !pageIds.includes(id))
                : Array.from(new Set([...prev, ...pageIds]))
        );
    };

    const handleAnalystSelect = (users) => {
        if (users && users.length > 0) {
            setSelectedAnalysts(users);
        }
    };

    const handleRemoveAnalyst = (operator) => {
        setSelectedAnalysts((prev) => prev.filter((u) => u.operator !== operator));
    };

    const handleAddClick = () => setScannerOpen(true);
    const handleEditClick = (record) => window.open(`/quality-control/temperature-tracker/${record.id}`, "_blank");

    const exportToPdf = (records) => {
        if (records.length === 0) {
            NotificationManager.warning("No records available to export.");
            return;
        }
        generateTemperatureTrackerPdf({
            records,
            startDate: searchCriteria.startDate,
            endDate: searchCriteria.endDate,
            searchText: searchCriteria.searchText,
            isSelection: records.length !== sortedData.length,
        });
    };

    const handleDeleteClick = (record) => {
        setSelectedRecord(record);
        setIsDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmationClose = () => {
        setIsDeleteConfirmationOpen(false);
        setSelectedRecord(null);
    };

    const handleDeleteConfirmationConfirm = async () => {
        try {
            setLoading(true);
            const response = await DeleteTemperatureRecord(selectedRecord.id);
            if (response.results === true) {
                NotificationManager.success("Record deleted successfully");
                setIsDeleteConfirmationOpen(false);
                setIsDeleted(!isDeleted);
                setSelectedRecord(null);
            } else {
                NotificationManager.error("Error deleting record");
            }
        } catch (error) {
            NotificationManager.error("Error deleting record");
        } finally {
            setLoading(false);
        }
    };

    const renderActions = (record) => (
        <div style={{ minWidth: "90px" }}>
            <Tooltip title="Edit Record">
                <IconButton size="small" style={{ color: "orange" }} onClick={() => handleEditClick(record)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Record">
                <IconButton size="small" style={{ color: "red" }} onClick={() => handleDeleteClick(record)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    );

    const renderCell = (col, record) => {
        if (col.id === "id") {
            return (
                <span
                    onClick={() => handleEditClick(record)}
                    style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", fontWeight: 500 }}
                >
                    {record[col.id]}
                </span>
            );
        }
        if (col.id === "dateCollected" && record[col.id]) {
            return new Date(record[col.id]).toLocaleString("en-US", {
                year: "numeric", month: "numeric", day: "numeric",
                hour: "numeric", minute: "2-digit",
            });
        }
        if (TEMPERATURE_COLUMNS.includes(col.id)) {
            const value = record[col.id];
            return value != null && value !== "" && !isNaN(Number(value))
                ? Number(value).toFixed(1)
                : "";
        }
        if (BOOLEAN_COLUMNS.includes(col.id)) {
            const value = record[col.id];
            return value == null ? "N/A" : value ? "Yes" : "No";
        }
        return record[col.id] != null ? String(record[col.id]).substring(0, 35) : "";
    };

    const fieldSx = { "& .MuiInputBase-root": { height: 40 } };

    return (
        <>

            <Box sx={{ mb: 2, mr: "20px", pt: 1 }}>
                <form onSubmit={handleSearchSubmit}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: { xs: "wrap", lg: "nowrap" },
                            gap: 1.5,
                            alignItems: "center",
                        }}
                    >
                        <Tooltip title="Search by Equipment or Type" arrow placement="top">
                            <TextField
                                label="Search Criteria"
                                variant="outlined"
                                size="small"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ ...fieldSx, flex: "1 1 160px", minWidth: 140 }}
                            />
                        </Tooltip>

                        <Tooltip title="Select one or multiple analysts" arrow placement="top">
                            <TextField
                                label="Analysts"
                                value={
                                    selectedAnalysts.length > 0
                                        ? `${selectedAnalysts.length} analyst${selectedAnalysts.length > 1 ? "s" : ""} selected`
                                        : ""
                                }
                                placeholder="Click to select analysts"
                                variant="outlined"
                                size="small"
                                onClick={() => setAnalystDialogOpen(true)}
                                InputProps={{
                                    readOnly: true,
                                    style: { cursor: "pointer" },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color={selectedAnalysts.length > 0 ? "success" : "primary"} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ ...fieldSx, width: 200, flexShrink: 0 }}
                            />
                        </Tooltip>

                        <Tooltip title="Filter records by Date Collected" arrow placement="top">
                            <TextField
                                label="From Date"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ ...fieldSx, width: 158, flexShrink: 0 }}
                            />
                        </Tooltip>

                        <Tooltip title="Filter records by Date Collected" arrow placement="top">
                            <TextField
                                label="To Date"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ ...fieldSx, width: 158, flexShrink: 0 }}
                            />
                        </Tooltip>

                        <Box sx={{ display: "flex", gap: 1, ml: "auto", flexShrink: 0 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ height: 40, minWidth: 100 }}
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddClick}
                                sx={{ height: 40, minWidth: 90, whiteSpace: "nowrap" }}
                            >
                                New
                            </Button>
                            <Tooltip
                                title={
                                    selectedRecords.length > 0
                                        ? `Export the ${selectedRecords.length} selected record(s) to PDF`
                                        : "Select one or more records to export"
                                }
                                arrow
                                placement="top"
                            >
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disabled={selectedRecords.length === 0}
                                        startIcon={<PictureAsPdfIcon />}
                                        onClick={() => exportToPdf(selectedRecords)}
                                        sx={{ height: 40, minWidth: 165, whiteSpace: "nowrap" }}
                                    >
                                        {selectedRecords.length > 0
                                            ? `Send to PDF (${selectedRecords.length})`
                                            : "Send to PDF"}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </form>

                {selectedAnalysts.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Selected Analysts:</strong>
                        {selectedAnalysts.map((user) => (
                            <Chip
                                key={user.operator}
                                label={`${user.operator.trim()} - ${(user.aliasName || "").trim()}`}
                                onDelete={() => handleRemoveAnalyst(user.operator)}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <Paper style={{ marginRight: "20px" }}>
                <TableContainer
                    ref={tableContainerRef}
                    sx={{
                        maxHeight: tableHeight,
                        overflowY: "auto",
                        overflowX: "auto",
                        width: "100%",
                        maxWidth: "100%",
                    }}
                >
                    <Table sx={{ minWidth: 400 }} size="small" stickyHeader>
                        <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main, color: "white" }}>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Tooltip title="Select all rows on this page" arrow placement="top">
                                        <Checkbox
                                            size="small"
                                            checked={allPageSelected}
                                            indeterminate={pageSelectedCount > 0 && !allPageSelected}
                                            onChange={handleTogglePage}
                                            sx={{
                                                color: "white",
                                                "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "white" },
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                {columns.map((col) => {
                                    const header = col.id === "actions" ? col.label : (
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : "asc"}
                                            onClick={() => handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    );
                                    return (
                                        <TableCell
                                            key={col.id}
                                            sx={{
                                                whiteSpace: "nowrap",
                                                ...(col.id === "actions" ? { ...STICKY_ACTIONS, zIndex: 3 } : null),
                                            }}
                                        >
                                            {col.tooltip ? (
                                                <Tooltip title={col.tooltip} arrow placement="top">
                                                    <span>{header}</span>
                                                </Tooltip>
                                            ) : header}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {pageData.map((record, index) => {
                                const isSelected = selectedIds.includes(record.id);
                                return (
                                    <TableRow
                                        key={record.id}
                                        hover
                                        selected={isSelected}
                                        sx={{
                                            backgroundColor: ROW_BG[index % 2],
                                            "&&.MuiTableRow-hover:hover": {
                                                backgroundColor: ROW_BG_HOVER,
                                            },
                                            "&&.Mui-selected, &&.Mui-selected:hover": {
                                                backgroundColor: ROW_BG_SELECTED,
                                            },
                                            "&:last-child td, &:last-child th": { border: 0 },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onChange={() => handleToggleRow(record.id)}
                                            />
                                        </TableCell>
                                        {columns.slice(0, -1).map((col) => (
                                            <TableCell
                                                key={col.id}
                                                sx={col.nowrap ? { whiteSpace: "nowrap" } : null}
                                            >
                                                {renderCell(col, record)}
                                            </TableCell>
                                        ))}
                                        {/* inherit picks up the row stripe, including hover/selected */}
                                        <TableCell sx={{ ...STICKY_ACTIONS, zIndex: 1, backgroundColor: "inherit" }}>
                                            {renderActions(record)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={sortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <DeleteConfirmation
                isOpen={isDeleteConfirmationOpen}
                onClose={handleDeleteConfirmationClose}
                onConfirm={handleDeleteConfirmationConfirm}
                title="Delete Confirmation"
                message="Are you sure you want to delete this temperature record?"
                color="red"
            />

            <TemperatureScannerDialog open={scannerOpen} onClose={() => setScannerOpen(false)} />

            <UserSelectorDialog
                open={analystDialogOpen}
                onClose={() => setAnalystDialogOpen(false)}
                onSelect={handleAnalystSelect}
                multiSelect={true}
            />

            <CommonLoader loading={loading} />
        </>
    );
};

export default TemperatureTrackerTable;
